import { createRow, MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useCallback, useEffect, useMemo, useState, version } from "react";
import { Box, Chip, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, COLORS, Icon, ICONS, Modal, TextField } from "abs-ui-components";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { useParams } from "react-router";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { API_ROUTES } from "../../ApiRoutes/ApiRoutes";

const ReviewAndUpdateLog = ({ ReviewAndUpdateLog, refetchMonitoringData, vesselMstId }) => {
	const [activeAction, setactiveAction] = useState(-1);
	const [validationErrors, setValidationErrors] = useState({});
	const [reviewAndUpdateData, setReviewAndUpdatedata] = useState([]);
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [deletingRowIndex, setDeletingRowIndex] = useState();
	const RowActions = {
		Edit: 0,
		Add: 1,
	};
	const handleDeleteBtnClick = (rowIndex) => {
		setConfirmDelete(true);
		setDeletingRowIndex(rowIndex);
	};

	useEffect(() => {
		if (Array.isArray(ReviewAndUpdateLog)) {
			setReviewAndUpdatedata(ReviewAndUpdateLog);
		} else {
			setReviewAndUpdatedata([]); // Fallback to empty array
		}
	}, [ReviewAndUpdateLog]);

	useEffect(() => {
		if (Array.isArray(ReviewAndUpdateLog)) {
			const transformedData = ReviewAndUpdateLog?.map((item) => {
				return {
					versionNumber: item.versionNumber || "",
					dateOrTimeLine: item.dateOrTimeLine || "",
					updatedParts: item.updatedParts || "",
					developedBy: item.developedBy || "",
					implementedBy: item.implementedBy || "",
				};
			});

			setReviewAndUpdatedata(transformedData);
		}
	}, [ReviewAndUpdateLog]);

	const resetActiveRow = () => {
		setactiveAction(-1);
		setValidationErrors({});
	};
	const validateRequired = (value) => {
		if (typeof value === "string") {
			return !!value?.length;
		} else if (typeof value === "number") {
			return value >= 0;
		}
		return false;
	};
	const validateField = (row) => {
		const errors = {};
        if (!validateRequired(row.versionNumber)) {
			errors.versionNumber = "Field is required";
		}
		if (!validateRequired(row.updatedParts)) {
			errors.updatedParts = "Field is required";
		}
		if (!validateRequired(row.developedBy)) {
			errors.developedBy = "Field is required";
		}
		if (!validateRequired(row.implementedBy)) {
			errors.implementedBy = "Field is required";
		}
		if (!row.dateOrTimeLine) {
			errors.dateOrTimeLine = "Date is required";
		}
		return errors;
	};

	const handleCreateReviewAndUpdateLog = async ({ values, table }) => {
		const newValidationErrors = validateField(values, "add", {});
		if (Object.values(newValidationErrors).some((error) => error)) {
			setValidationErrors(newValidationErrors);
			return;
		}
		setValidationErrors({});
		const newRow = {
			index: 0,
			versionNumber: values?.versionNumber,
			dateOrTimeLine: values?.dateOrTimeLine,
			updatedParts: values?.updatedParts,
			developedBy: values?.developedBy,
			implementedBy: values?.implementedBy,
		};
		const payload = reviewAndUpdateData?.map((item) => ({
			...item,
			index: 0,
		}));
		const finalPayload = [...payload, newRow];
		try {
			const reviewAndUpdateLog = await callApi({
				method: "POST",
				url: API_ROUTES.SeempiiiReviewAndUpdateLog(vesselMstId),
				data: finalPayload,
			});
			if (reviewAndUpdateLog?.isSuccess) {
				emitToasterMsg("success", "Table Review and update log", "has been added successfully");
				table.setCreatingRow(null);
				setReviewAndUpdatedata(finalPayload);
				setactiveAction(-1);
				if (typeof refetchMonitoringData === "function") {
					await refetchMonitoringData();
				}
			} else {
				emitToasterMsg("error", "Table Review and update log", reviewAndUpdateLog?.errorMessage);
			}
		} catch (error) {
			emitToasterMsg("error", "Table Review and update log", error.errorMessage);
		}
	};

	const handleUpdateReviewAndUpdateLog = async ({ values, table, row }) => {
		const newValidationErrors = validateField(values, "add", {});
		if (Object.values(newValidationErrors).some((error) => error)) {
			setValidationErrors(newValidationErrors);
			return;
		}
		setValidationErrors({});
		const updated = reviewAndUpdateData.map((item, index) => {
			if (index === row.index) {
				return {
					...item,
					versionNumber: values?.versionNumber,
					dateOrTimeLine: values?.dateOrTimeLine,
					updatedParts: values?.updatedParts,
					developedBy: values?.developedBy,
					implementedBy: values?.implementedBy,
				};
			}
			return item;
		});
		const finalPayload = updated.map((i) => ({ ...i, index: 0 }));

		try {
			const reviewAndUpdateLog = await callApi({
				method: "POST",
				url: API_ROUTES.SeempiiiReviewAndUpdateLog(vesselMstId),
				data: finalPayload,
			});
			if (reviewAndUpdateLog?.isSuccess) {
				emitToasterMsg("success", "Table Review and update log", "has been updated successfully");
				table.setEditingRow(null);
				setactiveAction(-1);
				setReviewAndUpdatedata(finalPayload);
				if (typeof refetchMonitoringData === "function") {
					await refetchMonitoringData();
				}
			} else {
				emitToasterMsg("error", "Table Review and update log", reviewAndUpdateLog?.errorMessage);
			}
		} catch (error) {
			emitToasterMsg("error", "Table Review and update log", error.errorMessage);
		}
	};

	const handleDeleteReviewAndUpdateLog = async (rowIndex) => {
		const updatedData = reviewAndUpdateData.filter((_, index) => index !== rowIndex);
		const finalPayload = updatedData?.map((item) => ({
			...item,
			index: 0,
		}));
		try {
			const reviewAndUpdateLog = await callApi({
				method: "POST",
				url: API_ROUTES.SeempiiiReviewAndUpdateLog(vesselMstId),
				data: finalPayload,
			});
			if (reviewAndUpdateLog?.isSuccess) {
				emitToasterMsg("success", "Table Review and update log", "has been deleted successfully");
				setactiveAction(-1);
                setConfirmDelete(false);
				setReviewAndUpdatedata(finalPayload);
				if (typeof refetchMonitoringData === "function") {
					await refetchMonitoringData();
				}
			} else {
				emitToasterMsg("error", "Table Review and update log", reviewAndUpdateLog?.errorMessage);
                setConfirmDelete(false);
			}
		} catch (error) {
			emitToasterMsg("error", "Table Review and update log", error.errorMessage);
		}
	};

	const handleReviewUpdateActions = (table, row, action) => {
		if (activeAction !== -1) return;

		if (action === RowActions.Add) {
			const defaultRow = createRow(table, {
				versionNumber: "",
				dateOrTimeLine: dayjs().toISOString(),
				updatedParts: "",
				developedBy: "",
				implemntedBy: "",
			});
			table.setCreatingRow(defaultRow);
			setactiveAction(action);
			return;
		}
		if (action === RowActions.Edit) {
			table.setEditingRow(row);
			setactiveAction(action);
		}
	};
	const updateData = (rowIndex, columnId, value) => {
		setReviewAndUpdatedata((prev) =>
			prev.map((row, index) => {
				if (index === rowIndex) {
					return {
						...row,
						[columnId]: value,
					};
				}
				return row;
			})
		);
	};
	const columns = useMemo(
		() => [
			{
				accessorKey: "versionNumber",
				header: "Version number",
				muiTableHeadCellProps: {
					sx: {
						borderBottom: "none",
						borderRight: "1px solid lightgray",
						verticalAlign: "middle",
					},
				},
				muiEditTextFieldProps: ({ cell, column, row, table }) => ({
					required: true,
					error: !!validationErrors?.versionNumber,
					helperText: validationErrors?.versionNumber,
					value: cell.getValue() ?? "",
					onChange: (e) => {
						const value = e.target.value;
						if (table.getState().creatingRow) {
							row._valuesCache[column.id] = value;
						} else {
							table.options.meta?.updateData?.(row.index, column.id, value);
						}
					},
					onFocus: () =>
						setValidationErrors({
							...validationErrors,
							versionNumber: undefined,
						}),
				}),
			},
			{
				accessorKey: "dateOrTimeLine",
				header: "Date/Timeline",
				muiTableHeadCellProps: {
					sx: {
						borderBottom: "none",
						borderRight: "1px solid lightgray",
						verticalAlign: "middle",
					},
				},
				Cell: ({ cell }) => (cell.getValue() ? dayjs(cell.getValue()).format("DD-MMM-YYYY") : ""),
				Edit: ({ cell, column, row, table }) => (
					<DatePicker
						value={cell.getValue() ? dayjs(cell.getValue()) : null}
						onChange={(newValue) => {
							const isoValue = newValue?.toISOString?.();
							if (table.getState().creatingRow) {
								row._valuesCache[column.id] = isoValue;
							} else {
								table.options.meta?.updateData?.(row.index, column.id, isoValue);
							}
						}}
						format="DD-MMM-YYYY"
						slotProps={{
							actionBar: { actions: ["today"] },
							textField: {
								sx: {
									"& .MuiInputBase-root": { height: 36 },
									"& input": {
										padding: "8px 10px",
										fontSize: "14px",
									},
								},
							},
						}}
					/>
				),
			},

			{
				accessorKey: "updatedParts",
				header: "Updated parts",
				muiTableHeadCellProps: {
					sx: {
						borderBottom: "none",
						borderRight: "1px solid lightgray",
						verticalAlign: "middle",
					},
				},
				muiEditTextFieldProps: ({ cell, column, row, table }) => ({
					required: true,
					error: !!validationErrors?.updatedParts,
					helperText: validationErrors?.updatedParts,
					value: cell.getValue() ?? "",
					onChange: (e) => {
						const value = e.target.value;
						if (table.getState().creatingRow) {
							row._valuesCache[column.id] = value;
						} else {
							table.options.meta?.updateData?.(row.index, column.id, value);
						}
					},
					onFocus: () =>
						setValidationErrors({
							...validationErrors,
							updatedParts: undefined,
						}),
				}),
			},
			{
				accessorKey: "developedBy",
				header: "Developed by",
				muiTableHeadCellProps: {
					sx: {
						borderBottom: "none",
						borderRight: "1px solid lightgray",
						verticalAlign: "middle",
					},
				},
				muiEditTextFieldProps: ({ cell, column, row, table }) => ({
					required: true,
					error: !!validationErrors?.developedBy,
					helperText: validationErrors?.developedBy,
					value: cell.getValue() ?? "",
					onChange: (e) => {
						const value = e.target.value;
						if (table.getState().creatingRow) {
							row._valuesCache[column.id] = value;
						} else {
							table.options.meta?.updateData?.(row.index, column.id, value);
						}
					},
					onFocus: () =>
						setValidationErrors({
							...validationErrors,
							developedBy: undefined,
						}),
				}),
			},

			{
				accessorKey: "implementedBy",
				header: "Implemeted By",
				muiTableHeadCellProps: {
					sx: {
						borderBottom: "none",
						borderRight: "1px solid lightgray",
						verticalAlign: "middle",
					},
				},
				muiEditTextFieldProps: ({ cell, column, row, table }) => ({
					required: true,
					error: !!validationErrors?.implementedBy,
					helperText: validationErrors?.implementedBy,
					value: cell.getValue() ?? "",
					onChange: (e) => {
						const value = e.target.value;
						if (table.getState().creatingRow) {
							row._valuesCache[column.id] = value;
						} else {
							table.options.meta?.updateData?.(row.index, column.id, value);
						}
					},
					onFocus: () =>
						setValidationErrors({
							...validationErrors,
							implementedBy: undefined,
						}),
				}),
			},
		],
		[validationErrors]
	);

	const table = useMaterialReactTable({
		columns,
		data: reviewAndUpdateData || [],
		meta: {
			updateData,
		},
		enableStickyHeader: true,
		enableSorting: false,
		enableRowSelection: false,
		enableColumnPinning: false,
		enableTopToolbar: true,
		enableBottomToolbar: false,
		enableColumnActions: false,
		createDisplayMode: "row",
		editDisplayMode: "row",
		enableEditing: true,
		enablePagination: false,
		muiTableHeadCellProps: {
			sx: {
				color: "rgb(255 255 255)",
				backgroundColor: "#042a4c",
				position: "sticky",
				borderRight: "1px solid lightgray",
				"&:last-child": {
					borderRight: "none",
				},
			},
		},
		muiTablePaperProps: {
			sx: {
				boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
			},
		},
		muiTableContainerProps: {
			sx: {
				maxHeight: "520px",
			},
		},
		displayColumnDefOptions: {
			"mrt-row-actions": {
				muiTableHeadCellProps: {
					sx: {
						borderBottom: "none",
						borderRight: "1px solid lightgray",
					},
				},
			},
		},
		muiTableBodyCellProps: ({ row, cell }) => {
			return {
				sx: {
					borderRight: "1px solid lightgray",
					"&:last-child": {
						borderRight: "none",
					},
					color: "",
				},
			};
		},
		muiTableBodyRowProps: ({ row }) => ({
			sx: {
				"&:not(:last-of-type)": {
					borderBottom: "1px solid lightgray",
				},
				verticalAlign: "top",
			},
		}),
		muiTableHeadRowProps: {
			sx: {
				borderBottom: "2px solid lightgray",
			},
		},
		columnOrder: ["dateOrTimeLine", "updatedParts", "developedBy", "implementedBy"],
		onCreatingRowSave: handleCreateReviewAndUpdateLog,
		onEditingRowCancel: resetActiveRow,
		onCreatingRowCancel: resetActiveRow,
		onEditingRowSave: handleUpdateReviewAndUpdateLog,
		renderToolbarInternalActions: ({ table }) => <></>,
		renderRowActions: ({ row, table }) => {
			return (
				<Box sx={{ display: "flex", gap: "2rem" }}>
					<Box>
						<Tooltip title="Edit" sx>
							<IconButton style={{ padding: "0px" }} onClick={() => handleReviewUpdateActions(table, row, RowActions.Edit)}>
								<EditIcon />
							</IconButton>
						</Tooltip>
					</Box>
					<Box>
						<Tooltip title="Delete">
							<IconButton style={{ padding: "0px" }} color="error" onClick={() => handleDeleteBtnClick(row?.index, true)}>
								<DeleteIcon />
							</IconButton>
						</Tooltip>
					</Box>
				</Box>
			);
		},
		renderTopToolbarCustomActions: ({ table }) => (
			<div className="mt-1">
				<Button
					startIcon={<Icon icon={ICONS.ADD} />}
					onClick={() => {
						handleReviewUpdateActions(table, null, RowActions.Add);
					}}
					disabled={!!table.getState().creatingRow}
				>
					Add Record
				</Button>
			</div>
		),
	});

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<div className="fuel-registry-b3 mt-1">
				<MaterialReactTable table={table} />
			</div>
			<div className="confirmation-modal-container">
				<Modal
					isOpen={confirmDelete}
					modalHeader
					padding={"70px"}
					headerHeight={"50px"}
					showFooter
					onCloseClickListener={() => {
						setConfirmDelete(false);
					}}
					footer={
						<div className="d-flex justify-content-end fixed-action-btns p-4">
							<Button
								className="modal-delete-btn"
								onClick={() => {
									handleDeleteReviewAndUpdateLog(deletingRowIndex);
								}}
								primaryColor={COLORS.RED.DARK}
								primaryBackgroundColor={COLORS.RED.LIGHT}
							>
								Delete
							</Button>
							<Button
								className="modal-cancel-btn"
								onClick={() => {
									setConfirmDelete(false);
								}}
							>
								Cancel
							</Button>
						</div>
					}
				>
					<div>Are you sure you want to delete this record?</div>
				</Modal>
			</div>
		</LocalizationProvider>
	);
};

export default ReviewAndUpdateLog;
